/**
 * Cíle přesměrování do starého IB přicházejí z odpovědi serveru — z hlavičky
 * `WWW-Authenticate` při challenge a z těla `POST /v1/url` u deep-linků.
 *
 * Obojí se dřív předávalo do `window.location.href` bez jakékoliv kontroly. To
 * je open redirect, a u `location.href` navíc sink na spuštění skriptu:
 * přiřazení `javascript:` URL ze same-origin skriptu se vykoná.
 *
 * Kontrola je záměrně rozdělená na dvě části s velmi různým profilem:
 *
 * - **schéma** se vynucuje. Nestojí na žádném předpokladu o infrastruktuře
 *   banky a zabíjí přesně tu eskalaci na spuštění skriptu.
 * - **doména** se jen loguje. Nevíme spolehlivě, na které hosty smí API
 *   přesměrovat — vynucovat odhad by znamenalo, že se při jeho změně nikdo
 *   nepřihlásí, a to je horší než riziko, které tím kryjeme.
 */

/**
 * Očekávaná doména odvozená z nakonfigurované base URL API, aby varování sedělo
 * i v dev a test prostředí a nebylo nikde zadrátované.
 *
 * Registrovanou doménu určuje odseknutím první části hostu (`ma.privatbanka.sk`
 * → `privatbanka.sk`). U víceúrovňových sufixů (`.co.uk`) by to bylo nepřesné;
 * na log hlášku to stačí a ušetří to tažení public suffix listu do bundlu.
 */
function expectedRedirectDomain(): string | null {
  const configured =
    window.__ENV__?.VITE_API_URL ?? import.meta.env.VITE_API_URL;
  if (!configured || configured.startsWith('__')) return null;

  try {
    const { hostname } = new URL(configured);
    const parts = hostname.split('.');
    return parts.length > 2 ? parts.slice(1).join('.') : hostname;
  } catch {
    return null;
  }
}

/**
 * Vrátí normalizovanou URL, pokud je cíl použitelný, jinak `null`.
 *
 * Vstup musí být ABSOLUTNÍ. Obě volající místa dostávají absolutní URL ze
 * serveru; rozpouštět relativní vstup proti vlastnímu originu by znamenalo, že
 * se jakýkoliv nesmysl v odpovědi tiše promění v navigaci po vlastní aplikaci.
 */
export function toSafeRedirectUrl(rawUrl: string): URL | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    console.error('[redirect] Cíl nelze zpracovat jako absolutní URL');
    return null;
  }

  // Vlastní origin je důvěryhodný z definice — uživatel z aplikace neodchází.
  // Tudy prochází i mock gateway v dev režimu, která běží na stejném originu
  // (a tedy i na http a localhostu).
  if (parsed.origin === window.location.origin) {
    return parsed;
  }

  // Jediná tvrdá podmínka. Zachytí javascript:, data:, blob: i prosté http:.
  // `javascript:` sem nespadne přes větev výše, protože jeho origin je 'null'.
  if (parsed.protocol !== 'https:') {
    console.error(
      `[redirect] Odmítnut cíl s nepovoleným schématem: ${parsed.protocol}`,
    );
    return null;
  }

  const expected = expectedRedirectDomain();
  const matchesExpected =
    parsed.hostname === expected || parsed.hostname.endsWith(`.${expected}`);
  if (expected && !matchesExpected) {
    console.warn(
      `[redirect] Cíl mimo očekávanou doménu ${expected}: ${parsed.origin}`,
    );
  }

  return parsed;
}
